import { useMutation } from '@apollo/client';

import { useCancellablePromise } from '../../../../../../../hooks/use-cancellable-promise';
import { useActionsInProgress } from '../../../../../../../graphql/preloader/actions/actions-in-progress';
import { promiseErrorCallbacks } from '../../../../../../../utils/promise/set-promise-error-callbacks';
import { CancelOrderNotificationModalProps } from './order-cancel-notification-modal.models';
import { UPDATE_NC_NOTIFICATION } from '../../../../../../../graphql/notifications/mutations/update-nc-notification';
import { OrderCancelNotificationModal as OrderCancelNotificationModalView } from './order-cancel-notification-modal';
import { useNavigate } from 'react-router-dom';
import { InternalLinks } from '../../../../../../../constants';
import { setOrderNumber } from '../../../../../../../graphql/order/order.cache';

export const OrderCancelModal = ({
  orderNumber, notificationId, onClose, ...props
}: CancelOrderNotificationModalProps): JSX.Element => {
  const [updateNotification] = useMutation(UPDATE_NC_NOTIFICATION);
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();
  const {
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  } = useCancellablePromise();

  const handleModalClose = async () => {
    try {
      addActionInProgress();

      await makeCancellablePromise(
        updateNotification({
          variables: {
            notificationId,
          },
        }),
      );
    } catch (error) {
      if (error instanceof CancelledPromiseOnUnmountError) {
        return;
      }

      if (promiseErrorCallbacks.anyError) {
        promiseErrorCallbacks.anyError();
      }
    } finally {
      removeActionInProgress();
      onClose!();
    }
  };

  const navigate = useNavigate();

  const handleCreate = () => {
    handleModalClose();
    navigate(InternalLinks.orderCreate, { state: { hasPreviousLocation: true } });
  };

  const handleDetails = () => {
    handleModalClose();
    navigate(InternalLinks.myOrdersPage, { state: { hasPreviousLocation: true } });
    setOrderNumber(orderNumber);
  };

  return (
    <OrderCancelNotificationModalView
      {...props}
      onClose={handleModalClose}
      onCreate={handleCreate}
      onDetails={handleDetails}
      orderNumber={orderNumber}
    />
  );
};
